import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Skeleton, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Chip, 
  TextField,
  MenuItem,
  Grid,
  Button,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../../services/api';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import { formatDate } from '../../utils/formatters';

interface MarginPosition {
  id: number;
  margin: string;
  fromAssetCode: string;
  toAssetCode: string;
  credit: string;
  userId: number;
  username?: string;
  orderType: string;
  quantity: string;
  leverage: string;
  entryPrice: string;
  status: string;
  createdAt: string;
}

interface MarginTrigger {
  id: number;
  marginOrderId: number;
  triggerPrice: string;
  triggerType: string;
  status: string;
  position?: MarginPosition;
}

interface MarginTriggerFilters {
  status: string;
  userId: string;
  orderType: string;
  page: number;
  limit: number;
}

interface MarginTriggersResponse {
  data: MarginTrigger[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const fetchMarginTriggers = async (filters: MarginTriggerFilters): Promise<MarginTriggersResponse> => {
  try {
    const params: any = {};
    
    if (filters.status) params.status = filters.status;
    if (filters.userId) params.userId = parseInt(filters.userId);
    if (filters.orderType) params.orderType = filters.orderType;
    
    params.limit = filters.limit;
    params.page = filters.page;

    const response = await api.get('/api/v1/admin-panel/margin/triggers', {
      params,
      headers: {
        'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
      }
    });

    return response.data.data;
  } catch (error) {
    console.error('Error fetching margin triggers:', error);
    throw error;
  }
};

const MarginTriggersPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triggers, setTriggers] = useState<MarginTrigger[]>([]);
  const [totalTriggers, setTotalTriggers] = useState(0);
  
  const [filters, setFilters] = useState<MarginTriggerFilters>({
    status: '',
    userId: '',
    orderType: '',
    page: 1,
    limit: 50
  });

  const loadTriggers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchMarginTriggers(filters);
      
      if (response?.data && Array.isArray(response.data)) {
        setTriggers(response.data);
        setTotalTriggers(response.total || 0);
      } else {
        console.error('Invalid response structure:', response);
        setTriggers([]);
        setTotalTriggers(0);
        setError('Не удалось загрузить триггеры: неверный формат данных');
      }
    } catch (err: any) {
      console.error('Error loading margin triggers:', err);
      setTriggers([]);
      setTotalTriggers(0);
      setError(err.response?.data?.message || err.message || 'Ошибка при загрузке триггеров');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTriggers();
  }, [filters]);

  const handleFilterChange = (field: keyof MarginTriggerFilters, value: string | number) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  const handleRefresh = () => {
    loadTriggers();
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      userId: '',
      orderType: '',
      page: 1,
      limit: 50
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'triggered':
        return 'success';
      case 'cancelled':
        return 'default';
      case 'liquidating':
        return 'warning';
      case 'liquidated':
        return 'error';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  const getTriggerTypeColor = (triggerType: string) => {
    switch (triggerType) {
      case 'liquidation':
        return 'error';
      case 'take_profit':
        return 'success';
      case 'stop_loss':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTriggerTypeLabel = (triggerType: string) => {
    switch (triggerType) {
      case 'liquidation':
        return 'Ликвидация';
      case 'take_profit':
        return 'Take Profit';
      case 'stop_loss':
        return 'Stop Loss';
      default:
        return triggerType;
    }
  };

  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <FilterListIcon sx={{ mr: 1 }} />
        Фильтры
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Статус"
            select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            size="small"
          >
            <MenuItem value="">Все статусы</MenuItem>
            <MenuItem value="active">Активен</MenuItem>
            <MenuItem value="triggered">Сработал</MenuItem>
            <MenuItem value="cancelled">Отменён</MenuItem>
            <MenuItem value="liquidating">Ликвидируется</MenuItem>
            <MenuItem value="liquidated">Ликвидирован</MenuItem>
            <MenuItem value="inactive">Неактивен</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="ID пользователя"
            value={filters.userId}
            onChange={(e) => handleFilterChange('userId', e.target.value)}
            size="small"
            type="number"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            label="Тип позиции"
            select
            value={filters.orderType}
            onChange={(e) => handleFilterChange('orderType', e.target.value)}
            size="small"
          >
            <MenuItem value="">Все типы</MenuItem>
            <MenuItem value="long">LONG</MenuItem>
            <MenuItem value="short">SHORT</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={clearFilters} size="small">
              Очистить
            </Button>
            <Button variant="contained" onClick={handleRefresh} size="small" startIcon={<RefreshIcon />}>
              Обновить
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderTriggersTable = () => {
    if (loading) {
      return (
        <Box>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} sx={{ mb: 1 }} />
          ))}
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          Ошибка при загрузке данных: {error}
        </Alert>
      );
    }

    if (!triggers || triggers.length === 0) {
      return (
        <Alert severity="info">
          Триггеры не найдены
        </Alert>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID триггера</TableCell>
            <TableCell>ID позиции</TableCell>
            <TableCell>Тип триггера</TableCell>
            <TableCell>Цена триггера</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Информация о позиции</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {triggers.map((trigger) => (
            <TableRow key={trigger.id}>
              <TableCell>{trigger.id}</TableCell>
              <TableCell>{trigger.marginOrderId}</TableCell>
              <TableCell>
                <Chip 
                  label={getTriggerTypeLabel(trigger.triggerType)} 
                  color={getTriggerTypeColor(trigger.triggerType)} 
                  size="small" 
                />
              </TableCell>
              <TableCell>{parseFloat(trigger.triggerPrice).toFixed(8)}</TableCell>
              <TableCell>
                <Chip 
                  label={trigger.status} 
                  color={getStatusColor(trigger.status)} 
                  size="small" 
                />
              </TableCell>
              <TableCell>
                {trigger.position ? (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="body2">
                        {trigger.position.fromAssetCode}/{trigger.position.toAssetCode} - 
                        ID: {trigger.position.userId}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        <Typography variant="caption" display="block">
                          <strong>Тип:</strong> {trigger.position.orderType === 'long' ? 'LONG' : 'SHORT'}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Количество:</strong> {parseFloat(trigger.position.quantity).toFixed(8)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Плечо:</strong> {trigger.position.leverage}x
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Цена входа:</strong> {parseFloat(trigger.position.entryPrice).toFixed(8)}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Статус позиции:</strong> {trigger.position.status}
                        </Typography>
                        <Typography variant="caption" display="block">
                          <strong>Создана:</strong> {formatDate(trigger.position.createdAt)}
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Информация недоступна
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Маржинальные триггеры
      </Typography>
      
      {renderFilters()}
      
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Найдено триггеров: {totalTriggers}
        </Typography>
        {renderTriggersTable()}
      </Paper>
    </Box>
  );
};

export default MarginTriggersPage;
